import { Component, Vue, Prop as prop, Watch as watch} from 'nuxt-property-decorator';
import debounce from 'lodash/debounce';
import { clone } from 'lodash';

@Component({
    components: {
        tooltip: require('~/components/common/tooltip/').default,
    },
})
export default class RangeSliderComponent extends Vue {

    @prop({
        type: Number,
    })
    public min: number;

    @prop({
        type: Number,
    })
    public max: number;

    @prop({
        type: Number,
    })
    public step: number;

    @prop()
    public value: [number, number];

    @prop({default: null})
    public type: string;

    @prop({default: null})
    public valueClass: string;

    @prop({default: false})
    private useDebounce: boolean;

    private pointers: number[] = [0, 1];

    private barStyle = {
        left: '0%',
        width: '100%',
    };

    private _step: number = 0;
    private _range: number = 0;
    private _rect: ClientRect;
    private _active: boolean = false;
    private correction = 8;

    private _debouncedInput = debounce((val) => {
        this.$emit('input', JSON.parse(JSON.stringify(val)));
    }, 300);

    private internalValue: [number, number] = [0, 0];

    public created() {

        this.internalValue = clone(this.value);
    }

    public mounted() {

        this._range = this.max - this.min;

        this.$set(this.pointers, 0, (this.internalValue[0] - this.min) / this._range);
        this.$set(this.pointers, 1, (this.internalValue[1] - this.min) / this._range);

        const min = Math.min(this.pointers[0], this.pointers[1]);
        const max = Math.max(this.pointers[0], this.pointers[1]);

        this.barStyle.left = (min * 100) + '%';
        this.barStyle.width = ((max - min) * 100) + '%';

        (this.$refs.range as HTMLElement).addEventListener('mousedown', this.touchstart, {
            passive: true,
        });

        (this.$refs.range as HTMLElement).addEventListener('mouseup', this.touchend, {
            passive: true,
        });
        (this.$refs.range as HTMLElement).addEventListener('mouseleave', this.touchend, {
            passive: true,
        });
    }

    public touchstart(event: MouseEvent) {
        this._step = this.step / this._range;
        this._rect = (this.$refs.range as HTMLElement).getBoundingClientRect();

        (this.$refs.range as HTMLElement).addEventListener('mousemove', this.touchMove, {
            passive: true,
        });

        this._active = true;

        this.touchMove(event);
    }

    public touchend(event: MouseEvent) {

        this._active = false;

        (this.$refs.range as HTMLElement).removeEventListener('mousemove', this.touchMove);
    }

    public touchMove(event: MouseEvent) {

        const cursorX = event.clientX

        let progress = (cursorX - (this._rect.left + this.correction)) / (this._rect.width - this.correction * 2);

        const mul = Math.round(progress / this._step);

        progress = Math.min(Math.max(0, this._step * mul), 1);

        if (this.pointers[0] === progress || this.pointers[1] === progress) {
            return;
        }

        if (Math.abs(this.pointers[0] - progress) < Math.abs(this.pointers[1] - progress)) {
            this.$set(this.pointers, 0, progress);
        } else {
            this.$set(this.pointers, 1, progress);
        }

        const min = Math.min(this.pointers[0], this.pointers[1]);
        const max = Math.max(this.pointers[0], this.pointers[1]);

        this.barStyle.left = (min * 100) + '%';
        this.barStyle.width = ((max - min) * 100) + '%';

        this.internalValue[0] = Math.round(this.min + min * this._range);
        this.internalValue[1] = Math.round(this.min + max * this._range);

        if (this.useDebounce) {
            this._debouncedInput(this.internalValue);
        } else {
            this.$emit('input', JSON.parse(JSON.stringify(this.internalValue)));
        }
    }

    @watch('value')
    private onValueChanged() {
        if (this.internalValue[0] !== this.value[0]
            || this.internalValue[1] !== this.value[1]) {
               this.internalValue = clone(this.value);
            }
    }
}
